import React from "react";
import { useTranslations } from "use-intl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type Row = Record<string, string | number>;

export type RowColumn = {
  key: string;
  label: string;
} & (
  | { kind: "number" | "text" }
  | { kind: "select"; options: { value: string; label: string }[]; defaultValue: string }
);

interface StructuredRowsEditorProps {
  /** The rows as the form holds them: a JSON array in a string. */
  value: string | undefined;
  onChange: (value: string) => void;
  columns: RowColumn[];
  newRow: Row;
  addLabel: string;
}

function parseRows(value: string | undefined): Row[] {
  try {
    const parsed: unknown = value ? JSON.parse(value) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** What a typed value becomes in the row: numbers are numbers, an emptied number is 0. */
function cellValue(column: RowColumn, raw: string): string | number {
  if (column.kind !== "number") return raw;
  return raw === "" ? 0 : Number(raw);
}

// Tailwind needs the full class name, so the column counts it supports are spelled out.
const GRID_COLUMNS: Record<number, string> = { 4: "grid-cols-4", 5: "grid-cols-5", 6: "grid-cols-6" };

/** An editable table of rows (VAT rates, payment methods, sales categories) kept as JSON in a form field. */
export function StructuredRowsEditor({ value, onChange, columns, newRow, addLabel }: StructuredRowsEditorProps) {
  const t = useTranslations("consultantDraft.rows");
  const rows = parseRows(value);
  const grid = `grid ${GRID_COLUMNS[columns.length + 1]} gap-2`;

  const commit = (next: Row[]) => onChange(JSON.stringify(next));
  const updateCell = (index: number, column: RowColumn, raw: string) => {
    commit(rows.map((row, i) => (i === index ? { ...row, [column.key]: cellValue(column, raw) } : row)));
  };

  return (
    <div className="space-y-2">
      {rows.map((row, index) => (
        <div key={index} className={`${grid} items-center`}>
          {columns.map((column) =>
            column.kind === "select" ? (
              <Select
                key={column.key}
                value={String(row[column.key] || column.defaultValue)}
                onValueChange={(v) => updateCell(index, column, v)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {column.options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                key={column.key}
                type={column.kind === "number" ? "number" : undefined}
                step={column.kind === "number" ? "0.01" : undefined}
                placeholder={column.label}
                value={row[column.key] ?? ""}
                onChange={(e) => updateCell(index, column, e.target.value)}
              />
            ),
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={t("remove")}
            onClick={() => commit(rows.filter((_, i) => i !== index))}
          >
            ×
          </Button>
        </div>
      ))}
      {rows.length > 0 && (
        <div className={`${grid} text-xs text-muted-foreground px-1`}>
          {columns.map((column) => <span key={column.key}>{column.label}</span>)}
          <span />
        </div>
      )}
      <Button type="button" variant="outline" size="sm" onClick={() => commit([...rows, newRow])}>
        {addLabel}
      </Button>
    </div>
  );
}
